import { useLazyQuery } from '@apollo/client';
import { Tag } from '@blueprintjs/core';
import { Autocomplete } from '@pi/ui';
import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import isMongoId from 'src/utils/isMongoId';
import { intersectQueries } from '@pi/shared-utils';

import type { AutocompleteProps, Field, MenuItemOption } from '@pi/ui';
import type { DocumentNode, QueryResult } from '@apollo/client';

export interface QueryAutocompleteProps<
    Item extends AnyDocument = AnyDocument,
    Query extends Record<string, any> = Record<string, any>,
    Variables extends Record<string, any> = Record<string, any>,
> extends Omit<AutocompleteProps, 'onChange' | 'value' | 'onSearch'> {
    baseQuery?: Record<string, any>;
    gqlQuery: DocumentNode;
    getOptions: (result: QueryResult<Query, Variables>) => ItemOption<Item>[];
    getVariables: (text: string) => Variables;
    onChange: (id: string | null, option: ItemOption<Item>) => void;
    value?: string | null;
    getSelectedLabel?: (option: ItemOption<Item>) => React.ReactNode;
    valid?: boolean;
    /**
     * Opt-in: returns an extra option for the raw search text (e.g. to allow a value that doesn't
     * exist in the search results), which is appended after the search results. Return
     * null/undefined to omit it.
     *
     * Also used (with an empty options list) to resolve the selected option when `value` is not
     * among the loaded results.
     */
    getCustomOption?: (
        text: string,
        options: ItemOption<Item>[],
    ) => ItemOption<Item> | null | undefined;
}

type AnyDocument = {
    _id: string;
    [key: string]: any;
};

export interface ItemOption<Item> extends MenuItemOption {
    data: Item;
}

export default function QueryAutocomplete<
    Query extends Record<string, any>,
    Variables extends Record<string, any>,
    Item extends AnyDocument = AnyDocument,
>({
    gqlQuery,
    getOptions,
    getVariables,
    onChange,
    value,
    baseQuery,
    getSelectedLabel = option => option.label,
    getCustomOption,
    ...props
}: QueryAutocompleteProps<Item, Query, Variables>): JSX.Element {
    const [callQuery, queryResult] = useLazyQuery<Query, Variables>(gqlQuery);
    const { loading, error, data } = queryResult;
    const selected = useMemo(() => {
        const options = data ? getOptions(queryResult) : [];
        const found = options.find(option => option.key === value);
        if (found || !value || !getCustomOption) return found;

        // not checked against the loaded options, since the value was already selected
        return getCustomOption(value, []) ?? undefined;
    }, [data, getOptions, queryResult, value, getCustomOption]);

    // eslint-disable-next-line no-console
    useEffect(() => error && console.error(error), [error]);

    useEffect(() => {
        if (!loading && !selected && !!value) {
            void callQuery({
                variables: getVariables(value!),
            });
        }
    }, [selected, loading, value, baseQuery, callQuery, getVariables]);

    const onSearch = useCallback(
        (text: string) => {
            const vars = getVariables(text);
            const variables = baseQuery
                ? { ...vars, query: intersectQueries(baseQuery, vars.query) }
                : vars;
            return callQuery({ variables }).then(result => {
                const options = getOptions(result);
                const custom = getCustomOption?.(text, options);
                return custom ? [...options, custom] : options;
            });
        },
        [callQuery, getOptions, getVariables, baseQuery, getCustomOption],
    );

    return (
        <Autocomplete
            triggerOnFocus
            placeholder={selected?.label}
            loading={loading}
            {...props}
            onChange={useCallback(
                (key: any, option: any) => onChange(key == null ? null : String(key), option),
                [onChange],
            )}
            onSearch={onSearch}
            inputProps={{
                ...props.inputProps,
                leftElement:
                    !loading && selected ? (
                        <Tag intent='primary'>{getSelectedLabel(selected)}</Tag>
                    ) : undefined,
            }}
        />
    );
}

export interface ModelAutocompleteProps<Item extends AnyDocument = AnyDocument>
    extends Omit<
        QueryAutocompleteProps<Item>,
        'gqlQuery' | 'getVariables' | 'getOptions' | 'getCustomOption'
    > {}

export interface ModelFieldAutoCompleteProps
    extends Omit<ModelAutocompleteProps, 'onChange' | 'value'> {
    field: string | Field<any>;
}

export function modelAutocompleteGetVariables(
    text: string,
    { searchField = 'name' }: { searchField?: string } = {},
): {
    query: Record<string, any>;
    sort: string;
} {
    const sort = searchField;
    text = String(text)?.trim() || '';

    return {
        sort,
        query: !text.length
            ? {}
            : text.length === 1
            ? { [searchField]: '~^' + text }
            : isMongoId(text)
            ? { _id: text }
            : { [searchField]: '~' + text },
    };
}

export const createGetOptions = _.memoize(
    <Item extends AnyDocument, Name extends string>(name: Name, labelField = 'name') =>
        (result: {
            data?: { [K in Name]?: { results?: any[] } | null } | null;
        }): ItemOption<Item>[] => {
            if (!result.data?.[name]?.results?.length) return [];

            return result.data![name]!.results!.map(
                data =>
                    ({
                        key: data._id,
                        label: data[labelField],
                        data,
                    } satisfies ItemOption<Item>),
            );
        },
);
